import type { CSSProperties } from 'react';
import type { CtaBlockProps, CtaSection } from '../types';
import { theme, sectionColors } from '../theme';
import { iconGlyph } from './FeatureGrid';

/** How many sections the band holds: '2'–'4' from the setting, anything else (blank, '1') = 1. */
export function ctaSectionCount(value: unknown): number {
  const n = Number(value);
  return Number.isInteger(n) && n >= 2 && n <= 4 ? n : 1;
}

/** The sections that show: the first `sectionCount` rows, less those switched off or with nothing in them. */
export function visibleCtaSections(props: Pick<CtaBlockProps, 'sectionCount' | 'sections'>): CtaSection[] {
  const n = ctaSectionCount(props.sectionCount);
  if (n === 1 || !Array.isArray(props.sections)) return [];
  return props.sections.slice(0, n).filter((s): s is CtaSection => !!s && typeof s === 'object'
    && s.show !== false
    && !!(s.icon?.trim() || s.body?.trim() || (s.buttonLabel?.trim() && s.buttonHref?.trim())));
}

/** Pure call-to-action band. */
export function CtaBlock(props: CtaBlockProps) {
  const { heading, body, buttonLabel, buttonHref, secondaryButtonLabel, secondaryButtonHref } = props;
  const secondary = secondaryButtonLabel?.trim() && secondaryButtonHref?.trim();
  // The band, and the readable on-colour for it — so the heading/body stay legible on a LIGHT
  // brand primary (the same invisible-white-text bug the footer had). The chip below inverts
  // whatever this resolves to.
  const colors = sectionColors(props, { bg: theme.buttonBg, text: theme.buttonText });
  // The same inverse chip for every button in the band (see the comment on the single band's).
  const chip = {
    '--blk-inverse-bg': colors.color,
    '--blk-inverse-fg': colors.backgroundColor,
  } as CSSProperties;

  // 2–4 sections under one shared heading. Their grid (blocks.css .blk-cta-sections) fits as many
  // side by side as the width allows, so it is the same in the Studio preview, on a laptop and on a
  // phone (one per row), with no breakpoint of the window deciding it.
  if (ctaSectionCount(props.sectionCount) > 1) {
    const sections = visibleCtaSections(props);
    return (
      <section className="px-6 py-16 text-center" style={{ ...colors, fontFamily: theme.bodyFont }} data-cta-sections={sections.length}>
        <h2 className="text-3xl font-semibold" style={{ fontFamily: theme.headingFont }}>{heading}</h2>
        {body ? <p className="mx-auto mt-3 max-w-xl opacity-80">{body}</p> : null}
        {sections.length > 0 ? (
          <div className="blk-cta-sections mx-auto mt-10 max-w-6xl">
            {sections.map((s, i) => {
              const left = s.iconPosition === 'left';
              const button = s.buttonLabel?.trim() && s.buttonHref?.trim();
              return (
                <div
                  key={i}
                  className={left ? 'flex items-start gap-4 text-left' : 'flex flex-col items-center text-center'}
                  data-cta-section={i + 1}
                  data-icon-position={left ? 'left' : 'top'}
                >
                  {s.icon?.trim() ? (
                    <div
                      className={`flex h-12 w-12 shrink-0 items-center justify-center rounded-full text-xl${left ? '' : ' mb-4'}`}
                      style={{ backgroundColor: 'color-mix(in srgb, currentColor 15%, transparent)' }}
                      aria-hidden="true"
                    >
                      {iconGlyph(s.icon)}
                    </div>
                  ) : null}
                  <div className={left ? 'min-w-0 flex-1' : 'flex flex-col items-center'}>
                    {s.body?.trim() ? <p className="opacity-90">{s.body}</p> : null}
                    {button ? (
                      <a
                        href={s.buttonHref}
                        className="blk-btn-inverse mt-5 inline-block rounded-md px-5 py-2.5 font-medium"
                        style={chip}
                      >
                        {s.buttonLabel}
                      </a>
                    ) : null}
                  </div>
                </div>
              );
            })}
          </div>
        ) : null}
      </section>
    );
  }

  return (
    <section className="px-6 py-16 text-center" style={{ ...colors, fontFamily: theme.bodyFont }}>
      <h2 className="text-3xl font-semibold" style={{ fontFamily: theme.headingFont }}>{heading}</h2>
      {body ? <p className="mx-auto mt-3 max-w-xl opacity-80">{body}</p> : null}
      {/* The band IS the button colour, so the chip is its INVERSE: filled with the button's
          label colour, lettered in the button's fill. That keeps today's look on a dark brand
          (buttonText resolves to white → a white chip) and fixes the light-brand case, where a
          hardcoded white chip sat invisibly on a pale band.
          It also makes this chip follow `button_primary_bg` the way the Brand Manager preview
          says it does — before, changing that role moved the chip's TEXT and left its fill white,
          so the map was telling the truth about the hero's search button and a half-truth here. */}
      <a
        href={buttonHref}
        className="blk-btn-inverse mt-8 inline-block rounded-md px-6 py-3 font-medium"
        // Inverts THIS band, not the global button role. A look whose CTA is painted from
        // `accent` (backgroundRole) resolves `colors` to the accent, so the chip takes its fill
        // and lettering from the resolved section or it stops contrasting with what it sits on.
        //
        // Passed as CUSTOM PROPERTIES, not as `backgroundColor`/`color`. An inline colour would
        // beat the `.blk-btn-inverse:hover` rule and leave the hover role inert — the same trap
        // that made the first version of this wiring do nothing. A custom property feeds the
        // class rule instead of competing with it.
        style={chip}
      >
        {buttonLabel}
      </a>
      {/* The optional second button: an outline in the band's own text colour, so it reads on
          any band without a colour of its own. */}
      {secondary ? (
        <a
          href={secondaryButtonHref}
          className="mt-8 ml-4 inline-block rounded-md border-2 px-6 py-3 font-medium opacity-90 hover:opacity-100"
          style={{ borderColor: 'currentColor' }}
        >
          {secondaryButtonLabel}
        </a>
      ) : null}
    </section>
  );
}
